import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { For } from "solid-js";
import type { PageMapLens } from "../../enums/map";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { nodeClass } from "./estateEdges";
import { wrapEstateLabel } from "./estateText";
import type { EstateNode } from "./estateTypes";
import { lensLabel, scoreLabel } from "./estateViewport";

export function EstateMapNode(props: {
  node: EstateNode;
  site: OverviewResponse["site"];
  lens: PageMapLens;
  compact: boolean | undefined;
  selectedPageId: string | null | undefined;
  activeCluster: string | null | undefined;
  contextPageId: string | null;
  connectedIds: Set<string>;
  routeFocusActive: boolean;
  focusedPageIds: Set<string>;
  hoveredPageId: string | null;
  onHover: (pageId: string | null) => void;
  onSelect: (pageId: string) => void;
}) {
  const label = () => pageDisplayLabel(props.node.page, props.site);
  const path = () => rawPagePath(props.node.page);
  const selected = () => props.selectedPageId === props.node.page.id;
  const hovered = () => props.hoveredPageId === props.node.page.id;
  const titleLines = () => wrapEstateLabel(label(), props.compact ? 24 : 27);
  const left = () => -props.node.width / 2;
  const top = () => -props.node.height / 2;
  const scoreText = () =>
    props.node.score === null ? "Not scored" : `${props.node.score}`;
  const issueText = () => {
    const count = `${props.node.issueCount} ${
      props.node.issueCount === 1 ? "issue" : "issues"
    }`;
    return props.node.important ? `! ${count}` : count;
  };

  return (
    /* biome-ignore lint/a11y/useSemanticElements: SVG map cards need role and keyboard handlers. */
    <g
      class={nodeClass(props.node, {
        selected: selected(),
        hovered: hovered(),
        contextId: props.contextPageId,
        connectedIds: props.connectedIds,
        activeCluster: props.activeCluster ?? null,
        routeFocusActive: props.routeFocusActive,
        focusedPageIds: props.focusedPageIds,
      })}
      data-page-id={props.node.page.id}
      data-cluster-key={props.node.cluster}
      transform={`translate(${props.node.x} ${props.node.y})`}
      role="button"
      tabIndex={0}
      aria-pressed={selected()}
      aria-label={`${label()}, ${path()}, ${lensLabel(props.lens)} score ${scoreLabel(
        props.node.score,
        props.lens,
      )}, ${props.node.issueCount} linked ${
        props.node.issueCount === 1 ? "issue" : "issues"
      }${props.node.important ? ", linked to a priority issue" : ""}`}
      onClick={() => {
        props.onHover(props.node.page.id);
        props.onSelect(props.node.page.id);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          props.onHover(props.node.page.id);
          props.onSelect(props.node.page.id);
        }
      }}
      onFocus={() => props.onHover(props.node.page.id)}
      onBlur={() => props.onHover(null)}
      onPointerMove={() => props.onHover(props.node.page.id)}
      onPointerLeave={() => props.onHover(null)}
    >
      <title>{`${label()} - ${path()} - ${scoreText()}`}</title>
      <rect
        class="signal-node-card"
        x={left()}
        y={top()}
        width={props.node.width}
        height={props.node.height}
        rx="1.05"
      />
      <rect
        class="signal-node-health-bar"
        x={left()}
        y={top()}
        width="0.72"
        height={props.node.height}
        rx="0.36"
      />
      <text class="signal-node-title" x={left() + 1.65} y={top() + 2.35}>
        <For each={titleLines()}>
          {(line, index) => (
            <tspan x={left() + 1.65} dy={index() === 0 ? 0 : 1.55}>
              {line}
            </tspan>
          )}
        </For>
      </text>
      <text class="signal-node-path" x={left() + 1.65} y={top() + 5.75}>
        {truncateMiddle(path(), props.compact ? 27 : 31)}
      </text>
      <g class="signal-node-score-badge">
        <rect
          x={left() + 1.45}
          y={top() + props.node.height - 2.35}
          width={props.node.score === null ? 6.4 : 4.3}
          height="1.45"
          rx="0.72"
        />
        <text
          x={left() + 1.45 + (props.node.score === null ? 6.4 : 4.3) / 2}
          y={top() + props.node.height - 1.3}
          text-anchor="middle"
        >
          {scoreText()}
        </text>
      </g>
      <text
        class="signal-node-issue-count"
        x={props.node.width / 2 - 1.4}
        y={top() + props.node.height - 1.26}
        text-anchor="end"
      >
        {issueText()}
      </text>
    </g>
  );
}

function truncateMiddle(value: string, limit: number): string {
  if (value.length <= limit) {
    return value;
  }
  const side = Math.floor((limit - 3) / 2);
  return `${value.slice(0, side)}...${value.slice(-side)}`;
}
